import React from "react";
import styled from "styled-components";
import produce from "immer";
import { useForm } from "react-hook-form";
import { useMutation, queryCache } from "react-query";
import { Button, Modal, Col } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { TInput } from "components/form/Field/TInput";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";

const createNewIssue = async ({ jobId, issue }) => {
  try {
    const res = await apiAxios.post(`/issues/jobs/${jobId}`, issue);
    return res.data.data;
  } catch (e) {
    throw e.response.data.message;
  }
};

const NewIssueModal = (props) => {
  // Component Props
  const { visible, onCancel, title, jobId, issueFor } = props;

  // Component State
  const [error, setError] = React.useState({});

  // Component Context
  const {
    state: notificationState,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  // Component Form
  const { handleSubmit, errors, control, reset, setValue } = useForm({
    defaultValues: {
      description: null,
    },
  });

  // Component mutation
  const [mutateCreateIssue] = useMutation(createNewIssue, {
    onSuccess: (issue, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        (oldData) => {
          const updatedDataWithNewIssue = produce(oldData, (draft) => {
            draft.issues = [issue, ...draft.issues];
          });
          return updatedDataWithNewIssue;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  // Component Function
  const onSubmit = async (data) => {
    setLoading("createNewIssue");
    try {
      await mutateCreateIssue({
        jobId,
        issue: { ...data, for: issueFor },
      });
      finishLoading("createNewIssue");
      reset();
      onCancel();
    } catch (error) {
      onCancel();
    }
  };

  return (
    <StyledModal
      title={title}
      visible={visible}
      onCancel={onCancel}
      destroyOnClose
      footer={null}
      header={null}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <TInput
          option="textarea"
          errors={errors}
          name="description"
          placeholder="What's the issue?"
          autoFocus={true}
          propsInput={{
            control: control,
          }}
        />
        <Col xs={24} align="end">
          <Button
            type="primary"
            htmlType="submit"
            icon={<ExclamationCircleOutlined />}
            loading={notificationState.loading.createNewIssue}
          >
            Report
          </Button>
        </Col>
      </form>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding-top: ${(props) => props.theme.spacing[2]};
    padding-bottom: ${(props) => props.theme.spacing[2]};
    padding-left: ${(props) => props.theme.spacing[2]};
    padding-right: ${(props) => props.theme.spacing[2]};
  }
`;

export default NewIssueModal;
